import { createTheme } from '@mui/material/styles'
import { adminColors, adminRadii, adminShadows } from './adminTokens'

const colors = adminColors
const radii = adminRadii
const shadows = adminShadows

const adminTheme = createTheme({
    palette: {
        primary: {
            main: colors.blue,
            dark: colors.blueDark,
            contrastText: colors.white,
        },
        secondary: {
            main: colors.gray700,
            contrastText: colors.white,
        },
        error: {
            main: colors.red,
            dark: colors.redDark,
            light: colors.redLight,
            border: colors.redBorder,
        },
        success: {
            main: colors.green,
            dark: colors.greenDark,
            light: colors.greenLight,
            contrastText: colors.white,
        },
        warning: {
            main: colors.orange,
            dark: colors.orangeDark,
            light: colors.orangeLight,
            contrastText: colors.white,
        },
        info: {
            main: colors.cyan,
            dark: colors.cyanDark,
            light: colors.cyanLight,
            contrastText: colors.white,
        },
        background: {
            default: colors.gray50,
            paper: colors.white,
        },
        action: {
            hover: colors.blueHover,
            selected: colors.blueSelected,
        },
        text: {
            primary: colors.gray900,
            secondary: colors.gray600,
        },
        divider: colors.gray200,
    },
    shape: {
        borderRadius: radii.surface,
    },
    typography: {
        fontFamily: [
            'Inter',
            'Pretendard',
            '-apple-system',
            'BlinkMacSystemFont',
            '"Segoe UI"',
            'Roboto',
            '"Noto Sans KR"',
            'sans-serif',
        ].join(','),
        h6: {
            color: colors.gray900,
            fontSize: '18px',
            fontWeight: 700,
            letterSpacing: 0,
            marginBottom: 16,
        },
        button: {
            fontWeight: 700,
            letterSpacing: 0,
            textTransform: 'none',
        },
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    backgroundColor: colors.gray50,
                    color: colors.gray900,
                    WebkitFontSmoothing: 'antialiased',
                    MozOsxFontSmoothing: 'grayscale',
                },
                '::selection': {
                    backgroundColor: colors.blueSelected,
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: colors.gray900,
                    boxShadow: shadows.appBar,
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                },
                rounded: {
                    borderRadius: radii.surface,
                },
                elevation1: {
                    boxShadow: shadows.surface,
                },
                outlined: {
                    borderColor: colors.gray200,
                },
            },
        },
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    borderRadius: radii.control,
                    minHeight: 34,
                    paddingLeft: 14,
                    paddingRight: 14,
                    fontWeight: 700,
                    lineHeight: 1.35,
                    boxShadow: 'none',
                    transition: 'background-color 120ms ease, border-color 120ms ease, color 120ms ease, box-shadow 120ms ease',
                    '&:focus-visible': {
                        boxShadow: `0 0 0 3px ${colors.blueSelected}`,
                    },
                    '&.Mui-disabled': {
                        color: colors.gray400,
                    },
                },
                containedPrimary: {
                    backgroundColor: colors.blue,
                    '&:hover': {
                        backgroundColor: colors.blueDark,
                    },
                },
                containedSecondary: {
                    backgroundColor: colors.gray900,
                    color: colors.white,
                    '&:hover': {
                        backgroundColor: colors.gray700,
                    },
                },
                containedSuccess: {
                    backgroundColor: colors.green,
                    '&:hover': {
                        backgroundColor: colors.greenDark,
                    },
                },
                containedWarning: {
                    backgroundColor: colors.orange,
                    '&:hover': {
                        backgroundColor: colors.orangeDark,
                    },
                },
                containedInfo: {
                    backgroundColor: colors.cyan,
                    '&:hover': {
                        backgroundColor: colors.cyanDark,
                    },
                },
                outlined: {
                    borderColor: colors.gray300,
                    color: colors.gray700,
                    '&:hover': {
                        borderColor: colors.gray400,
                        backgroundColor: colors.gray100,
                    },
                },
                outlinedPrimary: {
                    borderColor: colors.blue,
                    color: colors.blue,
                    '&:hover': {
                        borderColor: colors.blueDark,
                        backgroundColor: colors.blueHover,
                    },
                },
                text: {
                    color: colors.gray700,
                    '&:hover': {
                        backgroundColor: colors.gray100,
                    },
                },
                sizeSmall: {
                    minHeight: 30,
                    paddingLeft: 10,
                    paddingRight: 10,
                    fontSize: '13px',
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    borderRadius: radii.control,
                    color: colors.gray700,
                    transition: 'background-color 120ms ease, color 120ms ease, box-shadow 120ms ease',
                    '&:hover': {
                        backgroundColor: colors.gray100,
                        color: colors.gray900,
                    },
                    '&:focus-visible': {
                        boxShadow: `0 0 0 3px ${colors.blueSelected}`,
                    },
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: radii.control,
                    height: 26,
                    fontWeight: 700,
                    lineHeight: 1,
                },
                sizeSmall: {
                    height: 22,
                    fontSize: '12px',
                },
                label: {
                    paddingLeft: 9,
                    paddingRight: 9,
                },
                labelSmall: {
                    paddingLeft: 8,
                    paddingRight: 8,
                },
                colorDefault: {
                    backgroundColor: colors.gray100,
                    color: colors.gray700,
                },
                colorPrimary: {
                    backgroundColor: colors.blueSelected,
                    color: colors.blueDark,
                },
                colorSecondary: {
                    backgroundColor: colors.gray100,
                    color: colors.gray900,
                },
                colorSuccess: {
                    backgroundColor: colors.greenLight,
                    color: colors.greenDark,
                },
                colorWarning: {
                    backgroundColor: colors.orangeLight,
                    color: colors.orangeDark,
                },
                colorInfo: {
                    backgroundColor: colors.cyanLight,
                    color: colors.cyanDark,
                },
                colorError: {
                    backgroundColor: colors.redLight,
                    color: colors.redDark,
                },
                outlined: {
                    backgroundColor: colors.white,
                    borderColor: colors.gray300,
                },
                outlinedPrimary: {
                    borderColor: colors.blue,
                    color: colors.blueDark,
                    backgroundColor: colors.white,
                },
                outlinedSecondary: {
                    borderColor: colors.gray300,
                    color: colors.gray700,
                    backgroundColor: colors.white,
                },
                outlinedSuccess: {
                    borderColor: colors.greenBorder,
                    color: colors.greenDark,
                    backgroundColor: colors.white,
                },
                outlinedWarning: {
                    borderColor: colors.orangeBorder,
                    color: colors.orangeDark,
                    backgroundColor: colors.white,
                },
                outlinedInfo: {
                    borderColor: colors.cyanBorder,
                    color: colors.cyanDark,
                    backgroundColor: colors.white,
                },
                outlinedError: {
                    borderColor: colors.redBorder,
                    color: colors.redDark,
                    backgroundColor: colors.white,
                },
            },
        },
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    minHeight: 40,
                    paddingTop: 6,
                    paddingBottom: 6,
                    '&:hover': {
                        backgroundColor: colors.blueHover,
                    },
                },
            },
        },
        MuiListItemIcon: {
            styleOverrides: {
                root: {
                    minWidth: 40,
                    color: colors.gray600,
                },
            },
        },
        MuiListItemText: {
            styleOverrides: {
                primary: {
                    color: colors.gray900,
                    fontSize: '14px',
                    fontWeight: 600,
                },
            },
        },
        MuiBadge: {
            styleOverrides: {
                badge: {
                    fontWeight: 700,
                },
            },
        },
        MuiPaginationItem: {
            styleOverrides: {
                root: {
                    borderRadius: radii.control,
                    fontWeight: 600,
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                head: {
                    backgroundColor: colors.gray50,
                    color: colors.gray700,
                    fontWeight: 700,
                    borderBottomColor: colors.gray200,
                },
                body: {
                    color: colors.gray900,
                    borderBottomColor: colors.gray200,
                },
                sizeSmall: {
                    paddingTop: 8,
                    paddingBottom: 8,
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    transition: 'background-color 0.12s ease',
                    '& .MuiTableCell-root': {
                        lineHeight: 1.45,
                    },
                    'tbody &:hover': {
                        backgroundColor: colors.gray50,
                    },
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: radii.control,
                    backgroundColor: colors.white,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: colors.gray600,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: colors.blue,
                        borderWidth: 1,
                    },
                },
                notchedOutline: {
                    borderColor: colors.gray300,
                },
                input: {
                    color: colors.gray900,
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    color: colors.gray600,
                    '&.Mui-focused': {
                        color: colors.blue,
                    },
                },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    marginLeft: 0,
                    color: colors.gray600,
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: radii.surface,
                    minWidth: 360,
                    '@media (max-width:600px)': {
                        minWidth: 280,
                    },
                },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    color: colors.gray900,
                    fontSize: '18px',
                    fontWeight: 700,
                },
            },
        },
        MuiDialogActions: {
            styleOverrides: {
                root: {
                    padding: '12px 24px 20px',
                    gap: 8,
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                root: {
                    borderRadius: radii.control,
                    fontWeight: 600,
                    boxShadow: shadows.floating,
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    borderRadius: radii.control,
                    fontSize: '12px',
                    fontWeight: 600,
                    backgroundColor: colors.gray900,
                },
            },
        },
    },
})

export default adminTheme
